<template>
  <div class="bg-color-medium" :class="radiusClass" :style="{ width: cssWidth, height: cssHeight }" />
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from "vue";

export type SkeletonRadiusType = "xs" | "sm" | "md" | "lg" | "xl" | "full";

// Tailwind v4 scale: xs 2px, sm 4px, md 6px, lg 8px, xl 12px.
const RADIUS_CLASSES: Record<SkeletonRadiusType, string> = {
  xs: "rounded-xs",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
};

// Bare `rounded` (4px) is what every existing call site renders; keep it as the
// implicit default so adding `radius` does not restyle 49 skeletons.
const DEFAULT_RECT_RADIUS_CLASS = "rounded";

// A bare number ("70", 70, "22.5") is not valid CSS on its own — `width: 70` is
// dropped by the browser and the bar collapses. Callers scale dimensions
// arithmetically, so coerce unitless values to px rather than trusting them.
const UNITLESS = /^-?\d*\.?\d+$/;

function toCssLength(value: string | number): string {
  const raw = String(value).trim();
  return UNITLESS.test(raw) ? `${raw}px` : raw;
}

export default defineComponent({
  name: "SkeletonItem",
  props: {
    // BR-003 — variant scope widened in Wave 1 (retroactive AQ-8 reversal).
    // Audit behind original "rect-only" decision missed 13 files; PO approved
    // the narrow widen on 2026-04-23. `button` and `image` from EP map to
    // `rect` here (visually equivalent as rounded-rect placeholders).
    variant: { type: String as PropType<"rect" | "circle">, default: "rect" },
    width: { type: [String, Number], default: "100%" },
    height: { type: [String, Number], default: "1rem" },
    // Overrides the radius implied by `variant`, so a bar can match the corner
    // radius of whatever it stands in for.
    radius: { type: String as PropType<SkeletonRadiusType>, default: null },
  },
  setup(props) {
    const radiusClass = computed(() => {
      if (props.radius) {
        return RADIUS_CLASSES[props.radius];
      }
      return props.variant === "circle" ? RADIUS_CLASSES.full : DEFAULT_RECT_RADIUS_CLASS;
    });
    const cssWidth = computed(() => toCssLength(props.width));
    const cssHeight = computed(() => toCssLength(props.height));

    return { radiusClass, cssWidth, cssHeight };
  },
});
</script>
